const SCROLL_OFFSET = 0.1;

export default class Slider {

    allCells?: Array<HTMLElement>;
    selectedCells?: Array<HTMLElement>;
    selectedIndices?: Array<number>;
    prevElement?: HTMLElement;
    nextElement?: HTMLElement;

    carouselRef?: any;

    constructor(carouselRef) {

        this.allCells = [];
        this.selectedCells = [];
        this.selectedIndices = [];
        this.prevElement = null;
        this.nextElement = null;

        this.carouselRef = carouselRef;
    }

    // Helper getters for container boundaries
    private get containerLeft() {
        return this.carouselRef?.scrollLeft ?? 0;
    }

    private get containerRight() {
        const el = this.carouselRef;
        if (!el) return 0;
        return this.containerLeft + el.clientWidth;
    }

    private isFullyVisible(child: HTMLElement) {
        const left = child.offsetLeft;
        const right = left + child.offsetWidth;
        // Entirely inside container
        return left >= this.containerLeft && right <= this.containerRight;
    }

    init(callback?: Function) {
        this.updateSelectedCells();
        this.updatePrevNextElements();

        if (typeof callback === 'function') {
            callback({
                selectedCells: this.selectedCells,
                selectedIndicies: this.selectedIndices,
                nextElement: this.nextElement,
                prevElement: this.prevElement,
                initialIndex: this.selectedIndices[0],
            });
        }
    }

    getSliderProps() {
        return this;
    }

    updateSelectedCells() {
        const element = this.carouselRef;

        if (element) {
            // Convert HTMLCollections to Array
            this.allCells = Array.from(element.children);

            // Use scroll position instead of getBoundingClientRect for better off-screen support
            const selectedCells = Array.from(this.allCells).filter((child: HTMLElement) => {
                // treat any overlap as selected for UI purposes
                const childWidth = child.offsetWidth;
                const scrollOffset = childWidth * SCROLL_OFFSET; // maintain slight tolerance
                const left = child.offsetLeft;
                const right = left + childWidth;
                return right - scrollOffset > this.containerLeft && left + scrollOffset < this.containerRight;
            });

            if (selectedCells.length > 0) {
                this.selectedCells = selectedCells;
                this.selectedIndices = selectedCells.map((cell) => this.allCells.indexOf(cell));
            } else {
                // do we need to reset?
                this.selectedCells = [];
                this.selectedIndices = [];
            }
        }
    }

    scrollToElement(element, direction) {

        const slider = this.carouselRef;

        if (slider && element) {
            const elementRect = element.getBoundingClientRect();

            let newScrollPosition;
            const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);

            if (direction === 'left') {
                // Page-left: make the target element the last visible item
                newScrollPosition = element.offsetLeft - (elementRect.width * (this.selectedCells.length - 1));
                newScrollPosition = Math.max(0, newScrollPosition);
            } else {
                // Page-right: make the target element the first visible item
                newScrollPosition = Math.min(maxScrollLeft, Math.max(0, element.offsetLeft));
            }

            slider.scroll({
                left: newScrollPosition,
                behavior: 'smooth',
            });
        }
    }

    scrollRight() {
        this.scrollToElement(this.nextElement, 'right');
    };

    scrollLeft() {
        this.scrollToElement(this.prevElement, 'left');
    };

    updatePrevNextElements() {
        const firstVisibleIndex = this.selectedIndices[0];
        const lastVisibleIndex = this.selectedIndices[this.selectedIndices.length - 1];

        // Default prev/next based on indices
        this.prevElement = firstVisibleIndex > 0 ? this.allCells[firstVisibleIndex - 1] : null;
        this.nextElement = lastVisibleIndex < this.allCells.length - 1 ? this.allCells[lastVisibleIndex + 1] : null;

        // Edge-case: when the first item is partially visible, keep prev enabled
        if (firstVisibleIndex === 0) {
            const firstEl = this.allCells[0] as HTMLElement;
            if (firstEl && !this.isFullyVisible(firstEl)) {
                this.prevElement = firstEl; // allow one more left scroll to fully reveal
            }
        }

        // Edge-case: when the last item is only partially visible, keep next enabled
        if (lastVisibleIndex === this.allCells.length - 1) {
            const lastEl = this.allCells[lastVisibleIndex] as HTMLElement;
            if (lastEl && !this.isFullyVisible(lastEl)) {
                this.nextElement = lastEl; // allow one more right scroll to fully reveal
            }
        }
    };

    updateAfterScroll(callback?: Function) {

        const firstVisibleIndex = this.selectedIndices[0];

        this.init();

        const currentVisibleIndex = this.selectedIndices[0];

        const direction = firstVisibleIndex > currentVisibleIndex ? 'left' : 'right';

        const width = this.carouselRef?.clientWidth;

        if (typeof callback === 'function') {
            callback({
                direction,
                width,
                selectedCells: this.selectedCells,
                selectedIndicies: this.selectedIndices,
                nextElement: this.nextElement,
                prevElement: this.prevElement,
            });
        }
    }
}
